import React, { useCallback, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ARCHIVE_PROFILE_HINT_STORAGE_KEY,
  getArchiveProfileHintContent,
  TREE_HINT_ACTION_CLASS,
} from '../../utils/treeWorkspaceUi';
import { Button } from '../ui/button';

function readHintHidden() {
  try {
    return localStorage.getItem(ARCHIVE_PROFILE_HINT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Compact, dismissible tips for pets and chosen family on List View.
 */
export function ArchiveProfileHint({
  filterId,
  archiveMemberCounts,
  onFilterChange,
  className,
}) {
  const [hidden, setHidden] = useState(readHintHidden);
  const { title, copy } = getArchiveProfileHintContent(filterId);

  const dismiss = useCallback(() => {
    setHidden(true);
    try {
      localStorage.setItem(ARCHIVE_PROFILE_HINT_STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  const restore = useCallback(() => {
    setHidden(false);
    try {
      localStorage.removeItem(ARCHIVE_PROFILE_HINT_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  if (hidden) {
    return (
      <div className={cn('mb-4', className)}>
        <button
          type="button"
          onClick={restore}
          className="rounded-full border border-[hsl(38_14%_86%)] bg-[hsl(42_26%_99%)]/90 px-3 py-1.5 text-[10px] font-medium tracking-wide text-[hsl(32_10%_42%)] transition-colors hover:border-[hsl(38_14%_78%)] hover:bg-[hsl(42_26%_98%)] hover:text-[hsl(24_12%_22%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(43_28%_72%)]"
          data-testid="archive-profile-hint-restore"
        >
          Archive profile tips
        </button>
      </div>
    );
  }

  const showMemberSummary =
    filterId === 'all' &&
    archiveMemberCounts?.archiveMembers > 0 &&
    (archiveMemberCounts.pets > 0 || archiveMemberCounts.companions > 0);

  return (
    <div
      className={cn(
        'relative mb-3 rounded-lg border border-[hsl(38_16%_84%)]/80 bg-[hsl(42_28%_99%)]/90 px-3.5 py-2.5 pr-9 sm:px-4 sm:pr-10',
        className
      )}
      data-testid="archive-profile-hint"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:bg-[hsl(42_22%_94%)] hover:text-[hsl(24_12%_22%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(43_28%_72%)]"
        aria-label="Hide archive profile tips"
        data-testid="archive-profile-hint-dismiss"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
      <p className="font-serif text-sm font-medium text-foreground/85">{title}</p>
      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground/90">{copy}</p>
      {showMemberSummary ? (
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground/90">
          {archiveMemberCounts.pets > 0 && archiveMemberCounts.companions > 0
            ? `${archiveMemberCounts.pets} family pet${archiveMemberCounts.pets === 1 ? '' : 's'} and ${archiveMemberCounts.companions} chosen family or friend${archiveMemberCounts.companions === 1 ? '' : 's'} in this archive.`
            : archiveMemberCounts.pets > 0
              ? `${archiveMemberCounts.pets} family pet${archiveMemberCounts.pets === 1 ? '' : 's'} in this archive.`
              : `${archiveMemberCounts.companions} chosen family or friend${archiveMemberCounts.companions === 1 ? '' : 's'} in this archive.`}
        </p>
      ) : null}
      {showMemberSummary && onFilterChange ? (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {archiveMemberCounts.pets > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={TREE_HINT_ACTION_CLASS}
              onClick={() => onFilterChange('pets')}
            >
              View pets
            </Button>
          ) : null}
          {archiveMemberCounts.companions > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={TREE_HINT_ACTION_CLASS}
              onClick={() => onFilterChange('companions')}
            >
              View chosen family
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
